import styled, { keyframes } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../components/AppShell/styles";
import { FormGroup } from "../../../../components/Form/styles";
import { FireCta, Skeleton } from "../../../Domains/styles";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

export const Container = styled.div`
  padding-bottom: 2rem;
`;

/* A titled block of the screen: small uppercase label + card (same as /settings) */
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.5rem 2rem 0;

  @media ${MOBILE} {
    padding: 20px 20px 0;
  }
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 20px;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  /* The card's gap already spaces the fields */
  ${FormGroup} {
    margin-bottom: 0;
  }

  h3 {
    font-family: Poppins, sans-serif;
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.5rem;
    color: var(--color-neutral-white);
  }

  .lead {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--color-neutral-150);
  }

  /* A loading inside the card (e.g. a domain bought at Keepo being connected), centered */
  .loader {
    display: flex;
    justify-content: center;
    padding: 0.5rem 0;
  }
`;

/* Actions for the card above, right-aligned at its bottom */
export const CardFooter = styled.div`
  display: flex;
  gap: 1.25rem;
  align-items: center;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid var(--color-neutral-base);

  /* The Button's disabled look is the card's own color: lighter here, so it's still seen (not while it loads:
     then only Keepo's loading shows) */
  > div[disabled]:not([aria-busy="true"]) {
    color: var(--color-neutral-150);
    background-color: var(--color-neutral-base);
  }

  /* Loading on the card: the lighter gray under Keepo's loading (its own is the card's color) */
  > div[aria-busy="true"] {
    background-color: var(--color-neutral-base);
  }
`;

/* The setup instructions: an arrow per step, links underlined like the rest of the copy */
export const Steps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;

  li {
    display: flex;
    gap: 0.75rem;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 1px;
    stroke: var(--color-neutral-150);
  }

  a {
    font-weight: 700;
    color: var(--color-neutral-white);
    text-decoration: underline;
  }
`;

export const Note = styled.p`
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-neutral-150);
  background: rgba(255, 255, 255, 0.03);
  border-radius: 0.75rem;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const Spinner = styled.span`
  display: inline-block;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 100%;
  animation: ${spin} 0.8s linear infinite;
`;

/* Full-width main action at the end of the flow, like the reference screens */
export const MainAction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  padding: 1.5rem 2rem 0;

  @media ${MOBILE} {
    padding: 20px 20px 0;
  }

  /* The project's Button is a div */
  > :first-child {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
`;

export const TextLink = styled.button`
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-neutral-150);

  &:hover {
    color: var(--color-neutral-white);
    text-decoration: underline;
  }
`;

/* The domains bought at Keepo, right in the box: a header, then one row per domain with Connect (as the
   lists in /settings: rows apart by a thin line) */
export const DomainChoices = styled.div`
  overflow: hidden;
  font-family: Inter, sans-serif;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  header {
    padding: 1.25rem 20px 1rem;

    h3 {
      font-family: Poppins, sans-serif;
      font-size: 1.125rem;
      font-weight: 600;
      line-height: 1.5rem;
      color: var(--color-neutral-white);
    }

    p {
      margin-top: 0.25rem;
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: var(--color-neutral-150);
    }
  }

  li,
  .state {
    border-top: 1px solid var(--color-neutral-base);
  }

  /* Loading rows: on this box (neutral-dark) the placeholders are the lighter gray, or they'd vanish */
  ${Skeleton} {
    background: var(--color-neutral-base);
  }

  li {
    display: flex;
    gap: 0.875rem;
    align-items: center;
    padding: 0.875rem 20px;
  }

  .icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    color: var(--color-neutral-white);
    background: var(--color-brand-blue);
    border-radius: 10px;
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  /* The domain and its tag (free to connect, or connected to a Keepo) */
  .name {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 0.5rem;
    align-items: center;
    min-width: 0;

    b {
      overflow: hidden;
      font-size: 0.9375rem;
      font-weight: 700;
      color: var(--color-neutral-white);
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  /* Which Keepo it's on */
  .detail {
    overflow: hidden;
    font-size: 0.75rem;
    color: var(--color-neutral-150);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Loading, or none bought yet */
  .state {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 20px;
    text-align: center;

    p {
      font-size: 0.875rem;
      color: var(--color-neutral-150);
    }
  }
`;

/* A domain typed for another provider that nobody registered yet: offered at Keepo, above the field, as a
   domain row (icon, name and tag, price and Register); on phones the price and button go under it */
export const FreeDomainOffer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 0.875rem;
  align-items: center;
  padding: 0.875rem 1rem;
  font-family: Inter, sans-serif;
  background: var(--color-neutral-darkness);
  border: 1px solid var(--color-neutral-base);
  border-radius: 12px;

  .icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    color: var(--color-neutral-white);
    background: var(--color-brand-blue);
    border-radius: 10px;
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  .name {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    min-width: 0;

    b {
      overflow: hidden;
      font-size: 0.9375rem;
      font-weight: 700;
      color: var(--color-neutral-white);
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .detail {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  .buy {
    display: flex;
    flex-shrink: 0;
    gap: 0.75rem;
    align-items: center;
  }

  .price {
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    white-space: nowrap;
  }

  @media ${MOBILE} {
    .buy {
      flex: 1 1 100%;
      justify-content: space-between;
    }
  }
`;

/* The free domain's modal, in the Modal's Plus tone: the domain, its price and what comes with it */
export const FreeDomainModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .hero {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    align-items: center;
    text-align: center;

    p {
      max-width: 320px;
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: var(--color-neutral-150);
    }
  }

  .globe {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    color: var(--color-neutral-white);
    background: var(--color-brand-blue);
    border-radius: 1.25rem;
    box-shadow: 0 12px 30px rgba(75, 16, 255, 0.45);
  }

  .tag {
    padding: 0.1875rem 0.625rem;
    font-size: 0.6875rem;
    font-weight: 800;
    color: #0d3d1c;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: var(--color-brand-plus);
    border-radius: 100px;
  }

  h2 {
    max-width: 100%;
    font-family: Poppins, sans-serif;
    font-size: 1.375rem;
    font-weight: 700;
    line-height: 1.75rem;
    color: var(--color-neutral-white);

    .domain {
      display: block;
      overflow: hidden;
      color: var(--color-brand-plus);
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .price {
    display: flex;
    gap: 0.25rem;
    align-items: baseline;
    margin-top: 0.25rem;

    b {
      font-size: 1.75rem;
      font-weight: 800;
      color: var(--color-neutral-white);
    }

    span {
      font-size: 0.875rem;
      color: var(--color-neutral-150);
    }
  }

  .benefits {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.625rem;

    @media ${MOBILE} {
      grid-template-columns: 1fr;
    }

    li {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      padding: 0.625rem 0.75rem;
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--color-neutral-white);
      background: rgba(255, 255, 255, 0.04);
      border-radius: 0.75rem;
    }
  }
`;

/* Register with the fire ring takes the rest of the Modal's footer */
export const FreeDomainCta = styled(FireCta)`
  flex: 1;

  .cta {
    width: 100%;
    border-radius: 100px;
  }
`;
